import React, { memo, useEffect, useState } from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  withSequence,
  withDelay,
  Easing,
  cancelAnimation,
  SharedValue,
} from "react-native-reanimated";
import { colors, font } from "@/lib/theme";

const PLAYFUL_MESSAGES = [
  "Summoning the magic...",
  "Checking the stumps...",
  "Almost there...",
  "Polishing the bat...",
  "Rolling the pitch...",
  "Tossing the coin...",
  "Warming up...",
];

const smoothInOut = Easing.inOut(Easing.ease);
const smoothEase = Easing.bezier(0.4, 0, 0.2, 1);

type AnimationState = "verifying" | "success" | "error";

interface Props {
  title?: string;
  state?: AnimationState;
  errorMessage?: string;
}

const VerifyingAnimation: React.FC<Props> = memo(({
  title = "Verifying...",
  state = "verifying",
  errorMessage,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [displayedState, setDisplayedState] = useState<AnimationState>("verifying");

  const iconY = useSharedValue(0);
  const iconRotate = useSharedValue(0);
  const iconScale = useSharedValue(1);
  const iconOpacity = useSharedValue(1);

  const sparkle1Progress = useSharedValue(0);
  const sparkle2Progress = useSharedValue(0);
  const sparkle3Progress = useSharedValue(0);
  const sparkle4Progress = useSharedValue(0);

  const dot1Scale = useSharedValue(1);
  const dot2Scale = useSharedValue(1);
  const dot3Scale = useSharedValue(1);
  const dotsOpacity = useSharedValue(1);

  const messageOpacity = useSharedValue(1);
  const sparklesOpacity = useSharedValue(1);

  useEffect(() => {
    if (state === "verifying") {
      setDisplayedState("verifying");
      sparklesOpacity.value = withTiming(1, { duration: 300 });
      dotsOpacity.value = withTiming(1, { duration: 300 });
      iconOpacity.value = withTiming(1, { duration: 200 });

      iconY.value = withRepeat(
        withSequence(
          withTiming(-14, { duration: 1200, easing: smoothInOut }),
          withTiming(0, { duration: 1200, easing: smoothInOut }),
        ),
        -1, true
      );

      iconRotate.value = withRepeat(
        withSequence(
          withTiming(6, { duration: 800, easing: smoothInOut }),
          withTiming(-6, { duration: 1600, easing: smoothInOut }),
          withTiming(0, { duration: 800, easing: smoothInOut }),
        ),
        -1, false
      );

      iconScale.value = withRepeat(
        withSequence(
          withTiming(1.08, { duration: 1400, easing: smoothInOut }),
          withTiming(1, { duration: 1400, easing: smoothInOut }),
        ),
        -1, true
      );

      const sparkleLoop = (delay: number) =>
        withRepeat(
          withDelay(delay, withSequence(
            withTiming(0, { duration: 0 }),
            withTiming(1, { duration: 1600, easing: smoothEase }),
          )),
          -1, false
        );

      sparkle1Progress.value = sparkleLoop(0);
      sparkle2Progress.value = sparkleLoop(400);
      sparkle3Progress.value = sparkleLoop(800);
      sparkle4Progress.value = sparkleLoop(1200);

      const dotPulse = (delay: number) =>
        withDelay(delay, withRepeat(
          withTiming(1.4, { duration: 500, easing: smoothInOut }),
          -1, true
        ));

      dot1Scale.value = dotPulse(0);
      dot2Scale.value = dotPulse(170);
      dot3Scale.value = dotPulse(340);

      const messageInterval = setInterval(() => {
        messageOpacity.value = withSequence(
          withTiming(0, { duration: 200, easing: smoothEase }),
          withTiming(1, { duration: 200, easing: smoothEase })
        );
        setMessageIndex((prev) => (prev + 1) % PLAYFUL_MESSAGES.length);
      }, 2500);

      return () => clearInterval(messageInterval);
    } else {
      sparklesOpacity.value = withTiming(0, { duration: 300 });
      dotsOpacity.value = withTiming(0, { duration: 300 });

      cancelAnimation(iconY);
      cancelAnimation(iconRotate);
      cancelAnimation(iconScale);
      cancelAnimation(dot1Scale);
      cancelAnimation(dot2Scale);
      cancelAnimation(dot3Scale);

      iconY.value = withTiming(0, { duration: 300, easing: smoothEase });
      iconRotate.value = withTiming(0, { duration: 300, easing: smoothEase });
      iconScale.value = withTiming(1, { duration: 300, easing: smoothEase });

      iconOpacity.value = withSequence(
        withTiming(0, { duration: 150, easing: smoothEase }),
        withTiming(1, { duration: 150, easing: smoothEase })
      );

      setTimeout(() => setDisplayedState(state), 150);

      messageOpacity.value = withSequence(
        withTiming(0, { duration: 150, easing: smoothEase }),
        withTiming(1, { duration: 150, easing: smoothEase })
      );

      dot1Scale.value = withTiming(1, { duration: 200 });
      dot2Scale.value = withTiming(1, { duration: 200 });
      dot3Scale.value = withTiming(1, { duration: 200 });
    }
  }, [state]);

  const iconAnimatedStyle = useAnimatedStyle(() => ({
    opacity: iconOpacity.value,
    transform: [
      { translateY: iconY.value },
      { rotate: `${iconRotate.value}deg` },
      { scale: iconScale.value },
    ],
  }));

  const createSparkleStyle = (progress: SharedValue<number>) =>
    useAnimatedStyle(() => {
      const p = progress.value;
      const opacity = p < 0.2 ? p * 5 : p > 0.7 ? (1 - p) * 3.33 : 1;
      const translateY = -p * 25;
      const scale = p < 0.3 ? 0.6 + p * 1.33 : 1 - (p - 0.3) * 0.4;
      return {
        opacity: Math.max(0, Math.min(1, opacity)) * sparklesOpacity.value,
        transform: [{ translateY }, { scale: Math.max(0.4, scale) }],
      };
    });

  const sparkle1Style = createSparkleStyle(sparkle1Progress);
  const sparkle2Style = createSparkleStyle(sparkle2Progress);
  const sparkle3Style = createSparkleStyle(sparkle3Progress);
  const sparkle4Style = createSparkleStyle(sparkle4Progress);

  const dot1Style = useAnimatedStyle(() => ({ transform: [{ scale: dot1Scale.value }], opacity: dotsOpacity.value }));
  const dot2Style = useAnimatedStyle(() => ({ transform: [{ scale: dot2Scale.value }], opacity: dotsOpacity.value }));
  const dot3Style = useAnimatedStyle(() => ({ transform: [{ scale: dot3Scale.value }], opacity: dotsOpacity.value }));

  const titleStyle = useAnimatedStyle(() => ({ opacity: messageOpacity.value }));
  const messageStyle = useAnimatedStyle(() => ({ opacity: messageOpacity.value }));

  const getIcon = () => {
    switch (displayedState) {
      case "success":
        return (
          <View style={[s.iconBg, { backgroundColor: "#f0fdf4" }]}>
            <Text style={s.iconEmoji}>✅</Text>
          </View>
        );
      case "error":
        return (
          <View style={[s.iconBg, { backgroundColor: colors.dangerLight }]}>
            <Text style={s.iconEmoji}>😕</Text>
          </View>
        );
      default:
        return (
          <View style={[s.iconBg, { backgroundColor: colors.brand50 }]}>
            <Text style={s.iconEmoji}>✉️</Text>
          </View>
        );
    }
  };

  const getTitle = () => {
    switch (displayedState) {
      case "success": return "Success!";
      case "error": return "Verification Failed";
      default: return title;
    }
  };

  const getMessage = () => {
    switch (displayedState) {
      case "success": return "Redirecting you to the app...";
      case "error": return errorMessage || "Something went wrong. Please try again.";
      default: return PLAYFUL_MESSAGES[messageIndex];
    }
  };

  return (
    <View style={s.container}>
      <View style={s.iconArea}>
        <View style={s.scene}>
          <Animated.View style={[s.sparkle, { top: 0, left: 10 }, sparkle1Style]}>
            <Text style={{ fontSize: 20 }}>✨</Text>
          </Animated.View>
          <Animated.View style={[s.sparkle, { top: 15, right: 5 }, sparkle2Style]}>
            <Text style={{ fontSize: 18 }}>⭐</Text>
          </Animated.View>
          <Animated.View style={[s.sparkle, { bottom: 10, left: 20 }, sparkle3Style]}>
            <Text style={{ fontSize: 16 }}>🏏</Text>
          </Animated.View>
          <Animated.View style={[s.sparkle, { bottom: 25, right: 15 }, sparkle4Style]}>
            <Text style={{ fontSize: 20 }}>✨</Text>
          </Animated.View>

          <Animated.View style={[s.mailContainer, iconAnimatedStyle]}>
            {getIcon()}
          </Animated.View>
        </View>

        <View style={s.dotsContainer}>
          <Animated.View style={[s.dot, dot1Style]} />
          <Animated.View style={[s.dot, dot2Style]} />
          <Animated.View style={[s.dot, dot3Style]} />
        </View>
      </View>

      <View style={s.messageContainer}>
        <Animated.View style={[{ marginBottom: 8 }, titleStyle]}>
          <Text style={s.title}>{getTitle()}</Text>
        </Animated.View>
        <Animated.View style={[{ minHeight: 48, justifyContent: "center", paddingHorizontal: 20 }, messageStyle]}>
          <Text style={s.message}>{getMessage()}</Text>
        </Animated.View>
      </View>
    </View>
  );
});

VerifyingAnimation.displayName = "VerifyingAnimation";
export default VerifyingAnimation;

const s = StyleSheet.create({
  container: { flex: 1, alignItems: "center" },
  iconArea: { flex: 1, alignItems: "center", justifyContent: "center" },
  scene: { width: 200, height: 160, alignItems: "center", justifyContent: "center", position: "relative" },
  mailContainer: { position: "absolute", alignItems: "center", justifyContent: "center" },
  iconBg: { width: 96, height: 96, borderRadius: 48, alignItems: "center", justifyContent: "center" },
  iconEmoji: { fontSize: 44 },
  sparkle: { position: "absolute" },
  dotsContainer: { flexDirection: "row", marginTop: 24, gap: 8, height: 14 },
  dot: { width: 10, height: 10, borderRadius: 5, backgroundColor: colors.brand },
  messageContainer: { alignItems: "center", paddingHorizontal: 20, paddingBottom: 20 },
  title: { fontSize: 22, fontWeight: font.semibold, color: colors.ink },
  message: { fontSize: 15, color: colors.inkSecondary, textAlign: "center" },
});
